import { loadStripe, type PaymentIntentOrSetupIntentResult, type Stripe } from '@stripe/stripe-js';
import { call, put, select } from 'redux-saga/effects';
import { PAYMENT_ERROR_COPY } from '@/constants/paymentErrorCopy';
import actions from '@/redux/orderStatus/actions';
import paymentActions from '@/redux/payment/actions';
import { type RootState } from '@/types/app';
import { SubmitFailureCause } from '@/types/orderStatus';
import {
  buildVerificationResumeOrderData,
  maskPaymentIntentId,
  type PaymentVerificationChallenge,
  redactVerificationSecret,
  VERIFIED_PAYMENT_INTENT_STATUSES,
} from '@/utils/paymentVerification';
import { getBasketLogData, sentryLogger } from '@/utils/sentryLogger';

/**
 * TEAM-14331: run the diner's Stripe 3D Secure / SCA challenge for a submit coo-api answered with
 * `402 payment_verification_required`, then resubmit the same basket with the verified intent.
 *
 * Runs inside `orderSubmissionStartSaga`, so order status stays `TRANSMITTING` for the whole
 * challenge: Place Order stays disabled (AC5) and `LoadingPlaceOrder` stays up behind Stripe's
 * modal. That overlay sets no `role`, so it arms no focus trap to fight the challenge iframe.
 *
 * - **Verified:** re-dispatches `orderSubmissionStart` with the first leg's payload, the card
 *   carrying `payment_intent_id` in place of the spent token. That reuses the normal success path
 *   (confirmation, analytics, navigation) and skips `orderSubmissionTrigger`, so the basket is not
 *   re-validated or re-priced between the legs — chowly-api fails a resume whose amount moved. A
 *   failed resume takes the ordinary submission-error path, and `orderSubmissionStartSaga` resets
 *   the spent card token for it whatever the failure (not only code 11's decline text).
 * - **Not verified** (closed, failed, or Stripe.js could not run it): back to the payment step with
 *   the basket intact. The spent token is cleared so the card form returns; a fresh card on the same
 *   basket replaces the order chowly-api parked. Nothing is resubmitted (AC3).
 *
 * The challenge lives on the restaurant's Stripe Connect account, so Stripe.js is initialised
 * against `stripeAccount` — the checkout-wide instance (`stripe/hooks.ts`) is the platform one and
 * cannot see the intent. `handleNextAction`, not `handleCardAction`: chowly-api confirms the intent
 * server-side with automatic confirmation, which `handleCardAction` refuses.
 *
 * `clientSecret` never leaves this function: not into an action, redux, or a log line. Stripe's
 * error object is not logged either — its `payment_intent` carries the secret.
 */
export function* paymentVerificationSaga(
  challenge: PaymentVerificationChallenge,
  action: ReturnType<typeof actions.orderSubmissionStart>,
) {
  const state = (yield select()) as RootState;
  const publishableKey = state.app.paymentTypes?.credit_card_token?.key;
  const logData = {
    ...getBasketLogData(state.app.basket.checkoutBasket),
    paymentIntentId: maskPaymentIntentId(challenge.paymentIntentId),
    stripeAccountId: challenge.stripeAccountId,
  };

  sentryLogger.info('Payment verification required: starting the bank challenge', logData);

  let outcome: Record<string, string | undefined>;
  try {
    if (!publishableKey) {
      throw new Error('No Stripe publishable key for this location');
    }
    const stripe = (yield call(loadStripe, publishableKey, {
      stripeAccount: challenge.stripeAccountId,
    })) as Stripe | null;
    if (!stripe) {
      throw new Error('Stripe.js did not load');
    }

    const result = (yield call([stripe, stripe.handleNextAction], {
      clientSecret: challenge.clientSecret,
    })) as PaymentIntentOrSetupIntentResult;
    const paymentIntentStatus = result.paymentIntent?.status;

    if (
      !result.error &&
      paymentIntentStatus &&
      VERIFIED_PAYMENT_INTENT_STATUSES.includes(paymentIntentStatus)
    ) {
      sentryLogger.info('Payment verification succeeded: resubmitting the basket', {
        ...logData,
        paymentIntentStatus,
      });
      yield put(
        actions.orderSubmissionStart(
          buildVerificationResumeOrderData(action.submitBasketData, challenge.paymentIntentId),
          action.captcha,
          action.queryClient,
        ),
      );
      return;
    }

    outcome = {
      paymentIntentStatus,
      stripeErrorType: result.error?.type,
      stripeErrorCode: result.error?.code,
    };
  } catch (error) {
    outcome = {
      errorName: error instanceof Error ? error.name : undefined,
      errorMessage: error instanceof Error ? redactVerificationSecret(error.message) : undefined,
    };
  }

  // Expected and recoverable (a diner closing the challenge lands here), so warn, not error.
  sentryLogger.warn('Payment verification not completed', { ...logData, ...outcome });

  yield put(paymentActions.paymentTypeReset());
  yield put(
    actions.orderSubmissionError(
      PAYMENT_ERROR_COPY.cardVerificationFailed,
      SubmitFailureCause.UNCLASSIFIED,
    ),
  );
}
